import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Loader2, Send, MessageSquare, Bell, Settings2, RefreshCw, Search, Phone, Plus, ArrowLeft, User, X, ShieldOff, Trash2, UserPlus } from "lucide-react";

function SuppressionCard() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [num, setNum] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/sms/suppressions"); setList(data.suppressions || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!num.trim()) { toast.error("Enter a phone number"); return; }
    setBusy(true);
    try {
      await api.post("/sms/suppressions", { number: num.trim(), reason: reason.trim() || "manual" });
      toast.success("Number suppressed — we won't text it");
      setNum(""); setReason(""); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
    setBusy(false);
  };
  const remove = async (number) => {
    setList((l) => l.filter((x) => x.number !== number));
    try { await api.post("/sms/suppressions/remove", { number }); }
    catch { toast.error("Could not remove"); await load(); }
  };

  return (
    <div className="border border-border rounded-sm mb-6" data-testid="sms-suppression">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold" data-testid="sms-suppression-toggle">
        <span className="inline-flex items-center gap-2"><ShieldOff className="w-4 h-4 text-amber-accent" /> Suppressed numbers (do-not-text)</span>
        <span className="text-xs text-muted-foreground">{list.length} number{list.length === 1 ? "" : "s"}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
          <p className="text-[0.7rem] text-muted-foreground">Numbers here are never texted (replies, new messages or campaign nudges). Anyone who replies STOP/UNSUBSCRIBE is added automatically.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={num} onChange={(e) => setNum(e.target.value)} placeholder="+38640123456" className="h-9 text-sm sm:w-52" data-testid="sms-suppress-number" />
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (e.g. complained about spam)" className="h-9 text-sm flex-1" data-testid="sms-suppress-reason" />
            <Button onClick={add} disabled={busy} size="sm" className="rounded-sm" data-testid="sms-suppress-add">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Suppress"}</Button>
          </div>
          <div className="max-h-56 overflow-y-auto divide-y divide-border">
            {list.length === 0 ? <div className="text-xs text-muted-foreground py-4 text-center">No suppressed numbers.</div>
              : list.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-2 py-2" data-testid={`sms-suppress-row-${s.number}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-mono">{s.number}</div>
                    <div className="text-[0.65rem] text-muted-foreground truncate">{s.reason}{s.created_at ? ` · ${new Date(s.created_at).toLocaleDateString()}` : ""}</div>
                  </div>
                  <button onClick={() => remove(s.number)} className="shrink-0 p-1.5 rounded-sm text-muted-foreground hover:text-red-400 hover:bg-muted transition-colors" title="Remove" data-testid={`sms-suppress-remove-${s.number}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsCard({ onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ sms_sender: "", pushover_enabled: false, pushover_app_token: "", pushover_user_key: "", app_url: "" });
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/sms/config");
    setCfg(data);
    setForm((f) => ({ ...f, sms_sender: data.sms_sender || "", pushover_enabled: !!data.pushover_enabled, app_url: data.app_url || "" }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy("save");
    try {
      const payload = { sms_sender: form.sms_sender, pushover_enabled: form.pushover_enabled, app_url: form.app_url };
      if (form.pushover_app_token) payload.pushover_app_token = form.pushover_app_token;
      if (form.pushover_user_key) payload.pushover_user_key = form.pushover_user_key;
      await api.put("/sms/config", payload);
      toast.success("SMS settings saved");
      setForm((f) => ({ ...f, pushover_app_token: "", pushover_user_key: "" }));
      await load(); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setBusy("");
  };
  const testPush = async () => {
    setBusy("push");
    try { await api.post("/sms/config/test-push"); toast.success("Test push sent — check your phone"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Push failed"); }
    setBusy("");
  };

  if (!cfg) return null;
  return (
    <div className="border border-border rounded-sm mb-6" data-testid="sms-settings">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold" data-testid="sms-settings-toggle">
        <span className="inline-flex items-center gap-2"><Settings2 className="w-4 h-4 text-signal-500" /> SMS & Pushover settings</span>
        <span className="text-xs text-muted-foreground">{cfg.sms_sender || "no sender set"} · {cfg.pushover_enabled ? "alerts on" : "alerts off"}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
          <div>
            <label className="text-xs text-muted-foreground">SMS sender (Twilio number or ID)</label>
            <Input value={form.sms_sender} onChange={(e) => setForm({ ...form, sms_sender: e.target.value })} placeholder="+37258989449" className="mt-1 h-9 text-sm" data-testid="sms-sender-input" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm inline-flex items-center gap-2"><Bell className="w-4 h-4" /> Pushover alerts on new reply</span>
            <Switch checked={form.pushover_enabled} onCheckedChange={(v) => setForm({ ...form, pushover_enabled: v })} data-testid="pushover-toggle" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input value={form.pushover_app_token} onChange={(e) => setForm({ ...form, pushover_app_token: e.target.value })} placeholder={cfg.pushover_app_token_set ? "App token (saved — leave blank to keep)" : "Pushover app token"} className="h-9 text-sm" data-testid="pushover-token-input" />
            <Input value={form.pushover_user_key} onChange={(e) => setForm({ ...form, pushover_user_key: e.target.value })} placeholder={cfg.pushover_user_key_set ? "User key (saved — leave blank to keep)" : "Pushover user/group key"} className="h-9 text-sm" data-testid="pushover-key-input" />
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={save} disabled={busy === "save"} size="sm" className="rounded-sm" data-testid="sms-settings-save">{busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}</Button>
            <Button onClick={testPush} disabled={busy === "push"} size="sm" variant="outline" className="rounded-sm" data-testid="pushover-test">{busy === "push" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Send test push"}</Button>
            <span className="text-[0.7rem] text-muted-foreground ml-auto">Inbound webhook: <code>/api/webhooks/twilio/sms</code></span>
          </div>
        </div>
      )}
    </div>
  );
}

const initials = (s) => (s || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "#";
const dayLabel = (iso) => {
  const d = new Date(iso); const t = new Date();
  const same = (a, b) => a.toDateString() === b.toDateString();
  const y = new Date(t); y.setDate(t.getDate() - 1);
  if (same(d, t)) return "Today";
  if (same(d, y)) return "Yesterday";
  return d.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
};
const clock = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export default function SmsPage() {
  const [params, setParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);   // { key, prospect_id, peer, name }
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [composing, setComposing] = useState(false);
  const [newTo, setNewTo] = useState("");
  const [newBody, setNewBody] = useState("");
  const [linking, setLinking] = useState(false);
  const [cForm, setCForm] = useState({ first_name: "", last_name: "", company: "" });
  const [linkBusy, setLinkBusy] = useState(false);
  const scrollRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try { const { data } = await api.get("/sms/threads"); setThreads(data.threads || []); }
    catch { /* ignore */ }
    setLoading(false);
  }, []);
  const loadThread = useCallback(async (a) => {
    if (!a) return;
    try {
      const qs = a.prospect_id ? `prospect_id=${encodeURIComponent(a.prospect_id)}` : `peer=${encodeURIComponent(a.peer)}`;
      const { data } = await api.get(`/sms/thread?${qs}`);
      setThread(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => { if (active) loadThread(active); }, [active, loadThread]);

  // Restore from URL (?p= prospect / ?peer= number) once threads load.
  useEffect(() => {
    if (active || !threads.length) return;
    const p = params.get("p"); const peer = params.get("peer");
    const t = p ? threads.find((x) => x.prospect_id === p)
      : peer ? threads.find((x) => x.peer === peer) : null;
    if (t) setActive({ key: t.key, prospect_id: t.prospect_id, peer: t.peer, name: t.name });
  }, [threads, params, active]);

  // Auto-scroll to newest message.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread]);

  // Light polling for new messages every 20s.
  useEffect(() => {
    const id = setInterval(() => { loadThreads(); if (active) loadThread(active); }, 20000);
    return () => clearInterval(id);
  }, [active, loadThread, loadThreads]);

  // Escape closes the New SMS modal.
  useEffect(() => {
    if (!composing) return;
    const onKey = (e) => { if (e.key === "Escape") setComposing(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [composing]);

  const openThread = (t) => {
    setActive({ key: t.key, prospect_id: t.prospect_id, peer: t.peer, name: t.name });
    setParams(t.prospect_id ? { p: t.prospect_id } : { peer: t.peer });
  };

  const linkContact = async () => {
    if (!active?.peer) return;
    if (!cForm.first_name.trim() && !cForm.last_name.trim() && !cForm.company.trim()) {
      toast.error("Add a name/surname or a company"); return;
    }
    setLinkBusy(true);
    try {
      const { data } = await api.post("/sms/link-contact", { peer: active.peer, ...cForm });
      toast.success(`Contact saved${data.full_name ? ` — ${data.full_name}` : ""}`);
      setLinking(false); setCForm({ first_name: "", last_name: "", company: "" });
      await loadThreads();
      setActive({ key: data.prospect_id, prospect_id: data.prospect_id, peer: active.peer, name: data.full_name });
      setParams({ p: data.prospect_id });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save contact"); }
    setLinkBusy(false);
  };

  const send = async () => {
    if (!reply.trim() || !active) return;
    setBusy(true);
    try {
      const payload = active.prospect_id ? { prospect_id: active.prospect_id, body: reply } : { to: active.peer, body: reply };
      await api.post("/sms/reply", payload);
      setReply("");
      await loadThread(active); await loadThreads();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    setBusy(false);
  };

  const sendNew = async () => {
    if (!newTo.trim() || !newBody.trim()) { toast.error("Number and message required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/sms/send", { to: newTo, body: newBody });
      toast.success("SMS sent");
      setComposing(false); setNewTo(""); setNewBody("");
      await loadThreads();
      setActive({ key: data.prospect_id || data.peer, prospect_id: data.prospect_id, peer: data.peer, name: null });
      setParams(data.prospect_id ? { p: data.prospect_id } : { peer: data.peer });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    setBusy(false);
  };

  const filtered = threads.filter((t) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (t.name || "").toLowerCase().includes(q) || (t.peer || "").toLowerCase().includes(q) || (t.last_body || "").toLowerCase().includes(q);
  });

  const headerName = thread?.prospect?.full_name || thread?.prospect?.company || active?.name || active?.peer;
  const headerSub = thread?.peer || active?.peer;

  return (
    <DashboardLayout eyebrow="Messaging" title="SMS inbox"
      actions={<Button variant="outline" size="sm" className="rounded-sm" onClick={() => { loadThreads(); if (active) loadThread(active); }} data-testid="sms-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh</Button>}>
      <SettingsCard onSaved={loadThreads} />
      <SuppressionCard />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[calc(100vh-260px)] min-h-[480px]">
        {/* Conversation list */}
        <div className={`border border-border rounded-sm overflow-hidden md:col-span-1 flex flex-col ${active ? "hidden md:flex" : "flex"}`} data-testid="sms-thread-list">
          <div className="px-3 py-2.5 bg-muted/40 flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search conversations…" className="h-8 pl-8 text-xs" data-testid="sms-search" />
            </div>
            <button onClick={() => setComposing(true)} title="New SMS" className="shrink-0 w-8 h-8 rounded-sm bg-signal-500 text-background flex items-center justify-center hover:opacity-90" data-testid="sms-new-btn">
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? <div className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
              : filtered.length === 0 ? <div className="p-6 text-sm text-muted-foreground text-center">{search ? "No matches." : "No SMS conversations yet. Replies from prospects show up here."}</div>
              : filtered.map((t, i) => (
                <button key={t.key + i} onClick={() => openThread(t)}
                  data-testid={`sms-thread-${i}`}
                  className={`w-full text-left px-3 py-3 border-b border-border hover:bg-muted/30 transition-colors flex items-center gap-3 ${active?.key === t.key ? "bg-muted/50" : ""}`}>
                  <div className="shrink-0 w-10 h-10 rounded-full bg-signal-500/15 text-signal-500 flex items-center justify-center text-xs font-semibold">{initials(t.name || t.peer)}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm truncate">{t.name || t.peer}</span>
                      <span className="text-[0.6rem] text-muted-foreground whitespace-nowrap">{t.last_at ? clock(t.last_at) : ""}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                      <span className="text-xs text-muted-foreground truncate">{t.last_direction === "inbound" ? "" : "You: "}{t.last_body}</span>
                      {t.unread > 0 && <span className="shrink-0 text-[0.6rem] bg-signal-500 text-background rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center" data-testid={`sms-unread-${i}`}>{t.unread}</span>}
                    </div>
                  </div>
                </button>
              ))}
          </div>
        </div>

        {/* Chat pane */}
        <div className={`border border-border rounded-sm md:col-span-2 flex-col ${active ? "flex" : "hidden md:flex"}`} data-testid="sms-thread-view">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
              <div className="text-center"><MessageSquare className="w-6 h-6 mx-auto mb-2 opacity-50" />Select a conversation to view and reply</div>
            </div>
          ) : (
            <>
              <div className="px-4 py-2.5 border-b border-border flex items-center gap-3">
                <button className="md:hidden text-muted-foreground" onClick={() => { setActive(null); setThread(null); setParams({}); }}><ArrowLeft className="w-4 h-4" /></button>
                <div className="shrink-0 w-9 h-9 rounded-full bg-signal-500/15 text-signal-500 flex items-center justify-center text-xs font-semibold">{initials(headerName)}</div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{headerName}</div>
                  <div className="text-[0.7rem] text-muted-foreground inline-flex items-center gap-1"><Phone className="w-3 h-3" />{headerSub}{!active.prospect_id && <span className="ml-1 text-amber-accent">· not linked to a prospect</span>}</div>
                </div>
                {!active.prospect_id && (
                  <Button size="sm" variant="outline" className="rounded-sm ml-auto shrink-0" onClick={() => setLinking(true)} data-testid="sms-add-contact-btn">
                    <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Add contact
                  </Button>
                )}
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-1.5 bg-muted/10" data-testid="sms-messages">
                {(thread?.messages || []).length === 0 && <div className="text-center text-xs text-muted-foreground py-6">No messages yet.</div>}
                {(thread?.messages || []).map((m, idx) => {
                  const prev = (thread.messages || [])[idx - 1];
                  const showDay = !prev || new Date(prev.created_at).toDateString() !== new Date(m.created_at).toDateString();
                  const out = m.direction === "outbound";
                  return (
                    <div key={m.id || idx}>
                      {showDay && <div className="flex justify-center my-3"><span className="text-[0.6rem] uppercase tracking-wider bg-muted text-muted-foreground rounded-full px-2.5 py-0.5">{dayLabel(m.created_at)}</span></div>}
                      <div className={`flex ${out ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[78%] rounded-lg px-3 py-2 text-sm shadow-sm ${out ? "bg-emerald-600 text-white rounded-br-sm" : "bg-card border border-border rounded-bl-sm"}`}>
                          <div className="whitespace-pre-wrap break-words">{m.body}</div>
                          <div className={`text-[0.6rem] mt-1 text-right ${out ? "text-white/70" : "text-muted-foreground"}`}>{clock(m.created_at)}{out && m.status ? ` · ${m.status}` : ""}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="p-3 border-t border-border flex items-end gap-2">
                <textarea value={reply} onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  rows={1} placeholder="Type an SMS reply…  (Enter to send, Shift+Enter for a new line)"
                  className="flex-1 resize-none max-h-32 px-3 py-2 text-sm bg-background border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-signal-500" data-testid="sms-reply-input" />
                <Button onClick={send} disabled={busy || !reply.trim()} className="rounded-sm h-10 w-10 p-0" data-testid="sms-reply-send">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {composing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setComposing(false)}>
          <div className="bg-card border border-border rounded-sm w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="sms-new-modal">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-sm inline-flex items-center gap-2"><MessageSquare className="w-4 h-4 text-signal-500" /> New SMS</div>
              <button onClick={() => setComposing(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">To (phone number, incl. country code)</label>
              <Input value={newTo} onChange={(e) => setNewTo(e.target.value)} placeholder="+38640123456" className="mt-1 h-9 text-sm" data-testid="sms-new-to" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Message</label>
              <textarea value={newBody} onChange={(e) => setNewBody(e.target.value)} rows={4} className="mt-1 w-full px-3 py-2 text-sm bg-background border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-signal-500" data-testid="sms-new-body" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setComposing(false)}>Cancel</Button>
              <Button size="sm" className="rounded-sm" onClick={sendNew} disabled={busy || !newTo.trim() || !newBody.trim()} data-testid="sms-new-send">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1.5" />Send</>}
              </Button>
            </div>
          </div>
        </div>
      )}
      {linking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setLinking(false)}>
          <div className="bg-card border border-border rounded-sm w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="sms-link-modal">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-sm inline-flex items-center gap-2"><UserPlus className="w-4 h-4 text-signal-500" /> Add contact details</div>
              <button onClick={() => setLinking(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-[0.7rem] text-muted-foreground">Save a name, surname &amp; company for <span className="font-mono text-foreground">{active?.peer}</span>. This creates a prospect and links this conversation to it.</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-muted-foreground">First name</label>
                <Input value={cForm.first_name} onChange={(e) => setCForm({ ...cForm, first_name: e.target.value })} placeholder="Marko" className="mt-1 h-9 text-sm" data-testid="sms-link-first" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Surname</label>
                <Input value={cForm.last_name} onChange={(e) => setCForm({ ...cForm, last_name: e.target.value })} placeholder="Novak" className="mt-1 h-9 text-sm" data-testid="sms-link-last" />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Company</label>
              <Input value={cForm.company} onChange={(e) => setCForm({ ...cForm, company: e.target.value })} placeholder="Acme Logistics" className="mt-1 h-9 text-sm" data-testid="sms-link-company" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setLinking(false)}>Cancel</Button>
              <Button size="sm" className="rounded-sm" onClick={linkContact} disabled={linkBusy} data-testid="sms-link-save">
                {linkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-3.5 h-3.5 mr-1.5" />Save contact</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
